import { useState } from 'react';
import { FieldProps, RJSFSchema, toFieldPathId } from '@rjsf/utils';
import { Box, Button, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import Assignment from '@mui/icons-material/Assignment';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import { DynamicFormLeafletMap } from './dynamic-form-leaflet-map';
import { hasValidCoords, LocationFormValue } from './location-form-value';
import { FORM_ROW_SPACING } from './custom-object-field-template';

export function DynamicFormLocationField(props: FieldProps) {
  const [isEditing, setIsEditing] = useState(false);

  const formData = props.formData as LocationFormValue | undefined;
  const latitude = formData?.latitude;
  const longitude = formData?.longitude;
  const coordsText = hasValidCoords(latitude, longitude) ? `${latitude}, ${longitude}` : null;

  const { SchemaField } = props.registry.fields;
  const locationSchema = props.schema;

  const latSchema = (locationSchema.properties?.latitude as RJSFSchema) ?? { type: 'number', title: 'Latitude' };
  const lngSchema = (locationSchema.properties?.longitude as RJSFSchema) ?? { type: 'number', title: 'Longitude' };
  const { globalFormOptions } = props.registry;
  const latFieldPathId = toFieldPathId('latitude', globalFormOptions, props.fieldPathId);
  const lngFieldPathId = toFieldPathId('longitude', globalFormOptions, props.fieldPathId);

  return (
    <Grid container spacing={2}>
      <Grid size={6}>
        <Stack spacing={FORM_ROW_SPACING}>
          <SchemaField
            {...props}
            name="latitude"
            required={locationSchema.required?.includes('latitude') ?? false}
            schema={latSchema}
            uiSchema={{}}
            fieldPathId={latFieldPathId}
            formData={latitude}
            errorSchema={props.errorSchema?.latitude}
            onChange={props.onChange}
          />
          <SchemaField
            {...props}
            name="longitude"
            required={locationSchema.required?.includes('longitude') ?? false}
            schema={lngSchema}
            uiSchema={{}}
            fieldPathId={lngFieldPathId}
            formData={longitude}
            errorSchema={props.errorSchema?.longitude}
            onChange={props.onChange}
          />
        </Stack>
        {coordsText && (
          <Box style={{ marginTop: '12px' }}>
            <Typography variant="body2" style={{ color: '#888', marginBottom: '2px' }}>
              Geo-coordinates
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Typography variant="body2" style={{ color: '#555' }}>
                {coordsText}
              </Typography>
              <Tooltip title="Copy coordinates">
                <IconButton
                  size="small"
                  style={{ marginLeft: '4px', padding: '2px' }}
                  onClick={() => navigator.clipboard.writeText(coordsText)}
                >
                  <Assignment style={{ fontSize: '18px', color: '#888' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Grid>
      <Grid size={6}>
        <Box style={{ position: 'relative' }}>
          <DynamicFormLeafletMap
            latitude={latitude}
            longitude={longitude}
            readOnly={!isEditing}
            onCoordinatesChange={(lat, lng) =>
              props.onChange({ latitude: lat, longitude: lng } as LocationFormValue, props.fieldPathId.path)
            }
          />
          <Box style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 1000 }}>
            {isEditing ? (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<CheckIcon />}
                onClick={() => setIsEditing(false)}
              >
                Done
              </Button>
            ) : (
              <Button
                size="small"
                variant="contained"
                style={{ backgroundColor: 'white', color: '#555' }}
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
              >
                Edit location
              </Button>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
